import React, { useEffect } from "react";
import styled, { css, keyframes } from "styled-components";

import { IView } from "@formant/core/model/IView";
import { Uuid } from "@formant/core/model/Uuid";
import {
    defaultStoreId,
    RectangleStoreContextProvider,
} from "@formant/rectangle-sdk";
import { TextField } from "@formant/ui/component/common/TextField";
import {
    ModuleInspectorPanel,
    ViewInspectorPanel,
} from "@formant/views-editor";

import { useViewContext } from "../../../context/TeleopContext/ViewContext";
import { organizationStore } from "../../../stores";

interface IEditViewSidebar {
    view?: IView;
    deviceId: Uuid | undefined;
    isV2Dashboard?: boolean;
    onChangeViewId?: (viewId: Uuid) => void;
    onChangeView: (updates: Partial<IView>) => void;
}

export function EditViewSidebar(props: IEditViewSidebar) {
    const { view, onChangeView, isV2Dashboard = true, deviceId } = props;
    const {
        currentlyEditingNodeGraph,
        setCurrentlyEditingNodeGraph,
        isViewFocused,
    } = useViewContext();

    const organization = organizationStore.authenticated();

    if (!view || !view.id || !organization) {
        return null;
    }

    const [name, setName] = React.useState<string>(view.name || "");
    const [description, setDescription] = React.useState<string>(
        view.description || "",
    );

    useEffect(() => {
        setName(view.name || name || "");
        setDescription(view.description || description || "");
    }, [view.id]);

    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        onChangeView({ name: event.target.value.trim() });
    };

    const handleChangeDescription = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setDescription(event.target.value);
        onChangeView({ description: event.target.value });
    };

    return (
        <FormContainer>
            {!isV2Dashboard && (
                <>
                    <TextField
                        value={name}
                        onChange={handleChangeName}
                        label="Name"
                        data-testid="edit-view-name-input"
                    />
                    <TextField
                        value={description}
                        onChange={handleChangeDescription}
                        label="Description"
                        data-testid="edit-view-description-input"
                    />

                    <br />
                    <br />
                </>
            )}

            <RectangleStoreContextProvider storeId={defaultStoreId}>
                <AnimatedDiv
                    $slideIn={isV2Dashboard && isViewFocused}
                    $slideOut={!isV2Dashboard && isViewFocused}
                >
                    {isV2Dashboard && isViewFocused && (
                        <>
                            <ViewInspectorPanel
                                currentView={view as IView<"dashboard">}
                                isMultiDevice={
                                    !!view?.groupFilter?.tags && !deviceId
                                }
                                updateView={onChangeView}
                                ARChangeEnabled={organizationStore.getAiEnabled()}
                                VRChangeEnabled={organizationStore.getAiEnabled()}
                            />
                            <br />
                            <br />
                        </>
                    )}
                </AnimatedDiv>

                {currentlyEditingNodeGraph && (
                    <ModuleInspectorPanel
                        graph={currentlyEditingNodeGraph}
                        onChangeGraph={g => {
                            setCurrentlyEditingNodeGraph(g);
                        }}
                    />
                )}
            </RectangleStoreContextProvider>
        </FormContainer>
    );
}

const FormContainer = styled.div`
    padding: 1rem;
`;

const slideInRight = keyframes`
    from {
        transform: translateX(100%);
    }
    to {
        transform: translateX(0);
    }
`;

const slideOutLeft = keyframes`
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(-100%);
    }
`;

const AnimatedDiv = styled.div<{ $slideIn: boolean; $slideOut: boolean }>`
    ${props =>
        props.$slideIn &&
        css`
            animation: ${slideInRight} 0.5s forwards;
        `}
    ${props =>
        props.$slideOut &&
        css`
            animation: ${slideOutLeft} 0.5s forwards;
        `}
`;
